import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { dataSourceLinkSelectionSchema } from "../data-source-link-selection";

/**
 * Observe DataSource Link Trigger
 *
 * Entry point for workflows that react to row-level changes on a DataSource
 * Link's underlying table. Backed by Change Data Capture (Postgres logical
 * replication → Debezium → the `cdc-events` queue → CdcDispatchWorker → a durable
 * staging table → an in-process flusher).
 *
 * The trigger is intentionally a **notification + watermark + key manifest**, not
 * a row-data carrier: full row contents would be far too much to push through
 * Temporal for high-churn tables, and one-workflow-run-per-row does not scale.
 * Instead the flusher coalesces streamed changes per table and starts ONE run per
 * batch carrying only `{pk, op, lsn}` per change; downstream steps fetch the
 * rows they need by primary key from the ingest DB.
 *
 * The DataSource Link(s) to observe are chosen via the shared selection config
 * (`dataSourceLinkStrategy`): 'static' watches one specific link, while
 * 'matching_schema' watches every link conforming to a chosen SchemaDefinition —
 * subscribing to CDC for all of their tables and dispatching a run for whichever
 * table changed (each run still carries the single link that changed).
 *
 * Publication lifecycle: publishing a workflow that uses this trigger adds each
 * observed link's table to the shared CDC publication (`ALTER PUBLICATION ADD
 * TABLE`); the last workflow to stop watching a table removes it again
 * (reference-counted). So the configured selection decides which tables we capture.
 *
 * Two run phases (see `phase`):
 *   - "initial": one run emitted at publish time. `changes` is empty;
 *     `watermarkLsn` is the WAL position captured right AFTER streaming was
 *     switched on. Dependent steps use it to backfill (SELECT the table as-of the
 *     watermark) and establish their baseline. Changes at/after the watermark
 *     arrive as "change" runs, so backfill and stream meet with no gap and no
 *     double-count (reconcile by LSN).
 *   - "change": a batch of streamed changes — the `changes` manifest plus the
 *     batch's max `watermarkLsn`. The steady state.
 *
 * Ordering is NOT guaranteed across runs — dependent steps must use each change's
 * `lsn` for last-writer-wins, never arrival order. See
 * docs/workflows/observe-datasource-link-cdc-plan.md.
 */

// A single changed row, carrying its full image so downstream steps consume the
// data directly (no PK refetch). The flusher bounds each batch by row count,
// serialized size, and wait time so the run payload stays under Temporal's limit.
const rowChangeSchema = z.object({
  row: z
    .record(z.string(), z.unknown())
    .describe(
      "Full changed-row image: the new row for insert/update; for delete, the " +
        "prior row's identifying columns (PK under REPLICA IDENTITY DEFAULT, full row under FULL).",
    ),
  op: z
    .enum(["insert", "update", "delete"])
    .describe("Type of change operation"),
  lsn: z
    .string()
    .describe(
      "WAL LSN of this change as a numeric decimal string. Use for last-writer-wins ordering; arrival order is not guaranteed.",
    ),
});

const dataSourceLinkSchema = z.object({
  id: z.string().uuid().describe("DataSource Link id being observed"),
  dataSourceId: z.string().uuid().describe("Parent DataSource id"),
  schemaName: z.string().describe("Observed table schema"),
  tableName: z.string().describe("Observed table name"),
});

// One batch wrapped in a single object so the `changes` array is delivered as a
// unit and never auto-fans-out downstream (an array output socket would spawn one
// child run per change).
const cdcBatchEventSchema = z.object({
  phase: z
    .enum(["initial", "change"])
    .describe(
      "'initial': one publish-time run carrying the backfill watermark (changes is empty); 'change': a batch of streamed row changes.",
    ),
  watermarkLsn: z
    .string()
    .describe(
      "High WAL LSN (numeric decimal string) this batch covers. On the initial run it is the snapshot watermark — backfill rows as-of it and treat changes at/after it as the stream. On change runs it is the max LSN in the batch.",
    ),
  changes: z
    .array(rowChangeSchema)
    .describe(
      "Batch of changed rows (full row image + op + lsn); empty on the initial run.",
    ),
});

export const observeDataSourceLinkStep = defineStepConfig({
  type: "observe_data_source_link",
  name: "Observe DataSource Link",
  description:
    "Triggers the workflow on batched row-level changes to a DataSource Link's table (Change Data Capture). Each run carries a batch of changed rows (full row image + op + lsn) + a watermark LSN. A special initial run carries the backfill watermark.",
  category: "Trigger",

  // Entry point — no inputs. The change manifest / initial watermark arrive via
  // the orchestrator's initial inputs (set by the CDC flusher / the publish hook).
  inputSchema: z.never(),

  outputSchema: z.object({
    dataSourceLink: dataSourceLinkSchema.describe(
      "The DataSource Link this run observed",
    ),
    cdcBatchEvent: cdcBatchEventSchema.describe(
      "One CDC batch as a single object (phase + watermark + changes), so the " +
        "batch flows downstream as a unit instead of fanning out per change.",
    ),
  }),

  configSchema: dataSourceLinkSelectionSchema.extend({
    operationsToWatch: z
      .array(z.enum(["insert", "update", "delete"]))
      .optional()
      .describe(
        "Which DML operations should trigger a run. Defaults to all three.",
      ),
    batchSize: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(
        "Maximum number of changes carried by a single run's `changes` manifest. " +
          "The flusher dispatches a run as soon as this many changes have accumulated. " +
          "Defaults to 500.",
      ),
    maxWaitMs: z
      .number()
      .int()
      .min(0)
      .optional()
      .describe(
        "Maximum time (milliseconds) to wait for more changes before dispatching a " +
          "partial batch (fewer than batchSize). Bounds dispatch latency when change " +
          "volume is low. Defaults to 2000. (Resolution is bounded by the flusher tick.)",
      ),
  }),

  emits: [] as const,
});

export type ObserveDataSourceLinkConfig = z.infer<
  typeof observeDataSourceLinkStep.configSchema
>;
